import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Kunde } from './kunde.service';

export interface Standort {
  id: string;
  nummer: number;
  name: string;
  status: number;
  kunde_id: string; // Direct relationship to client
  techniker_id: number; // Foreign key to mitarbeiter table (where is_techniker = true)
  bemerkung_struktur_anmerkung: string;
  zugriffsrechte: number;
  // Only set by the worklist filter (?vertrag=ohne_vertrag|ohne_service)
  rv_erf_nr?: string | null;
  rv_service_artikel_nummer?: string | null;
  rv_vk_wert_netto?: string | number | null;
  bericht_sprache: string;
  anmeldung_besuche: string;
  bericht_versand: string;
  besuchsbericht: number;
  mit_auswertung_tabelle: number;
  kontaktadressen: number;
  herkunft: string;
  anrede: string;
  bericht_email?: string; // E-Mail-Adresse für Berichtsversand
  
  // New date fields
  datum_ab_wann?: string;
  datum_bis?: string;
  
  // Address fields
  strasse?: string;
  plz?: string;
  ort?: string;
  land?: string;
  soll_viertel?: number | null;

  // Technician text (BMD Kopftext import)
  kopftext_raw?: string;
  zugang?: string;
  kontakt_text?: string;
  boxen?: string;
  objekt_notiz?: string;
  
  // Timestamp fields
  created_at?: string;
  updated_at?: string;
  
  // Relationships (loaded via with())
  kunde?: Kunde; // Client object
  techniker?: {
    id: number;
    vorname?: string;
    nachname?: string;
    nummer?: string;
    funktion?: string;
  }; // Techniker (mitarbeiter) object
  active_projektaufgabe?: {
    id: string;
    periodizitaet: number;
    periodizitat_serie?: string;
    beschreibung?: string;
  } | null; // Active projektaufgabe for this standort
}

/** One saved version of the Standort Technikerinfos (full snapshot of all five fields). */
export interface TechnikerinfoRevision {
  id: string;
  standort_id: string;
  zugang?: string | null;
  kontakt_text?: string | null;
  boxen?: string | null;
  objekt_notiz?: string | null;
  bemerkung_struktur_anmerkung?: string | null;
  changed_by_mitarbeiter_id?: string | null;
  changed_by_name?: string | null;
  source?: string | null;
  created_at: string;
}

/** Editable Technikerinfos payload (PUT /standort/{id}/technikerinfos). */
export interface TechnikerinfoFields {
  zugang?: string | null;
  kontakt_text?: string | null;
  boxen?: string | null;
  objekt_notiz?: string | null;
  bemerkung_struktur_anmerkung?: string | null;
}

export interface Kontakt {
  id: string;
  name: string;
  email: string;
}

// Account interface removed - not used in standort

export interface StandortResponse {
  result: number;
  data: Standort[];
  total?: number;
  page?: number;
  limit?: number;
  sec: string;
}

export interface StandortSingleResponse {
  result: number;
  data: Standort;
  sec: string;
}

@Injectable({
  providedIn: 'root'
})
export class StandortService {
  private apiUrl = environment.apiUrl;
  private allStandorteForFilter$?: Observable<StandortResponse>;

  constructor(private http: HttpClient) { 
    console.log('StandortService: Constructor called');
    console.log('StandortService: apiUrl set to:', this.apiUrl);
    console.log('StandortService: http client available:', !!this.http);
  }

  /**
   * Resolve SVG symbol URL for an indicator code using environment mapping
   */
  getIndicatorSymbolUrl(code?: number | null): string | null {
    if (code == null) return null;
    const key = (environment as any).indikatorSymbolMap?.[code];
    if (!key) return null;
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.svg`;
  }

  /**
   * Get symbol color for an indicator code using environment mapping
   */
  getIndicatorSymbolColor(code?: number | null): string | null {
    if (code == null) return null;
    const colorMap = (environment as any).indikatorSymbolColorMap || {};
    return colorMap[code] || null;
  }

  /**
   * Get all standorte with pagination
   */
  getStandorte(page: number = 1, limit: number = 10, search: string = '', sortBy: string = 'name', sortOrder: string = 'asc', kundeId?: string, statusId?: number, technikerId?: number, prefix?: string, vertrag?: string): Observable<StandortResponse> {
    const params: any = {
      page: page.toString(),
      limit: limit.toString(),
      search: search,
      sortBy: sortBy,
      sortOrder: sortOrder
    };
    
    if (kundeId) {
      params.kunde_id = kundeId;
    }
    
    if (statusId !== undefined && statusId !== null) {
      params.status_id = statusId.toString();
    }
    
    if (technikerId !== undefined && technikerId !== null) {
      params.techniker_id = technikerId.toString();
    }
    
    if (prefix) {
      params.prefix = prefix;
    }

    if (vertrag) {
      params.vertrag = vertrag;
    }
    
    console.log('StandortService: Making API call to', `${this.apiUrl}/standort`, 'with params:', params);
    return this.http.get<StandortResponse>(`${this.apiUrl}/standort`, { params });
  }

  /**
   * Get every standort for filter dropdowns, cached for the session.
   *
   * This is an 8 MB response and list pages were refetching it on every
   * visit, alongside their own list request. A newly created standort only
   * shows up in the filter after a reload, which is an acceptable trade.
   */
  getAllStandorteForFilter(): Observable<StandortResponse> {
    if (!this.allStandorteForFilter$) {
      this.allStandorteForFilter$ = this.getStandorte(1, 10000).pipe(
        // Drop the cache on failure, otherwise shareReplay would replay the
        // error for the rest of the session.
        catchError(error => {
          this.allStandorteForFilter$ = undefined;
          return throwError(() => error);
        }),
        shareReplay(1)
      );
    }
    return this.allStandorteForFilter$;
  }

  /**
   * Get single standort by ID
   */
  getStandort(id: string): Observable<StandortSingleResponse> {
    return this.http.get<StandortSingleResponse>(`${this.apiUrl}/standort/${id}`);
  }

  /**
   * Create new standort
   */
  createStandort(standort: Partial<Standort>): Observable<StandortSingleResponse> {
    return this.http.post<StandortSingleResponse>(`${this.apiUrl}/standort`, standort);
  }

  /**
   * Update standort
   */
  updateStandort(id: string, standort: Partial<Standort>): Observable<StandortSingleResponse> {
    return this.http.put<StandortSingleResponse>(`${this.apiUrl}/standort/${id}`, standort);
  }

  /**
   * Update the Technikerinfos only (Zugang, Kontakt, Boxen, Objekt-Notiz,
   * Bemerkung Zutritt Kontakt). Every save is kept as a revision server-side.
   */
  updateTechnikerinfos(
    id: string,
    fields: TechnikerinfoFields
  ): Observable<{ result: number; data: TechnikerinfoFields & { latest_technikerinfo_revision?: TechnikerinfoRevision | null } }> {
    return this.http.put<{ result: number; data: any }>(`${this.apiUrl}/standort/${id}/technikerinfos`, {
      ...fields,
      source: 'office'
    });
  }

  /**
   * Full Technikerinfos history for a standort, newest first
   */
  getTechnikerinfoRevisions(id: string): Observable<{ result: number; data: TechnikerinfoRevision[] }> {
    return this.http.get<{ result: number; data: TechnikerinfoRevision[] }>(
      `${this.apiUrl}/standort/${id}/technikerinfos/revisions`
    );
  }

  /**
   * Delete standort
   */
  deleteStandort(id: string): Observable<StandortSingleResponse> {
    return this.http.delete<StandortSingleResponse>(`${this.apiUrl}/standort/${id}`);
  }

  /**
   * Get standorte by kunde ID
   */
  getStandorteByKundeId(kundeId: string, page: number = 1, limit: number = 10): Observable<StandortResponse> {
    console.log('StandortService: getStandorteByKundeId called with kundeId:', kundeId, 'page:', page, 'limit:', limit);
    return this.getStandorte(page, limit, '', 'name', 'asc', kundeId);
  }

  /**
   * Get all standort status for dropdown
   */
  getStandortStatus(): Observable<any> {
    // This would need to be implemented in the backend
    // For now, return a mock response
    return new Observable(observer => {
      observer.next({
        result: 1,
        data: [
          { id: 1, code: 'AKTIV', beschreibung: 'Aktiv' },
          { id: 2, code: 'INAKTIV', beschreibung: 'Inaktiv' },
          { id: 3, code: 'IN_BEARBEITUNG', beschreibung: 'In Bearbeitung' },
          { id: 4, code: 'GESPERRT', beschreibung: 'Gesperrt' }
        ]
      });
      observer.complete();
    });
  }

  /**
   * Get all niederlassung for dropdown
   */
  getNiederlassungen(): Observable<any> {
    // This would need to be implemented in the backend
    // For now, return a mock response
    return new Observable(observer => {
      observer.next({
        result: 1,
        data: [
          { code: 'WIEN', bezeichnung: 'Wien' },
          { code: 'GRAZ', bezeichnung: 'Graz' },
          { code: 'LINZ', bezeichnung: 'Linz' },
          { code: 'SALZBURG', bezeichnung: 'Salzburg' },
          { code: 'INNSBRUCK', bezeichnung: 'Innsbruck' }
        ]
      });
      observer.complete();
    });
  }

  /**
   * Get all kontakte for dropdown
   */
  getKontakte(): Observable<any> {
    // This would need to be implemented in the backend
    // For now, return a mock response
    return new Observable(observer => {
      observer.next({
        result: 1,
        data: [
          { id: '1', name: 'Max Mustermann', email: 'max@example.com' },
          { id: '2', name: 'Anna Schmidt', email: 'anna@example.com' },
          { id: '3', name: 'Peter Müller', email: 'peter@example.com' }
        ]
      });
      observer.complete();
    });
  }

  /**
   * Print single indicator QR label (HTML preview)
   */
  printQRIndicatorLabel(indicatorId: string): Observable<string> {
    // Use the updated endpoint that uses qr_etiketten.html.twig template
    const url = `${this.apiUrl}/pdf-qr-indicator/${indicatorId}?html=1`;
    return this.http.get(url, {
      responseType: 'text'
    });
  }

  /**
   * Generate single indicator QR label PDF
   */
  generateQRIndicatorLabelPDF(indicatorId: string): Observable<Blob> {
    // Use the updated endpoint that uses qr_etiketten.html.twig template
    const url = `${this.apiUrl}/pdf-qr-indicator/${indicatorId}`;
    return this.http.get(url, {
      responseType: 'blob'
    });
  }

  /**
   * Print all indicators QR labels for a standort (HTML preview)
   */
  printQRIndicatorLabels(standortId: string): Observable<string> {
    // Use the updated endpoint that uses qr_etiketten.html.twig template
    const url = `${this.apiUrl}/pdf-qr-indicators/${standortId}?html=1`;
    return this.http.get(url, {
      responseType: 'text'
    });
  }

  /**
   * Generate all indicators QR labels PDF
   */
  generateQRIndicatorLabelsPDF(standortId: string): Observable<Blob> {
    // Use the updated endpoint that uses qr_etiketten.html.twig template
    const url = `${this.apiUrl}/pdf-qr-indicators/${standortId}`;
    return this.http.get(url, {
      responseType: 'blob'
    });
  }

  /**
   * Export standort as PDF (with or without plan document)
   */
  exportStandortAsPDF(standortId: string, planImageBase64?: string | null, useSavedImage?: boolean): Observable<Blob> {
    // Use POST if base64 data is provided and large (to avoid URL length limits)
    if (planImageBase64 && planImageBase64.length > 2000) {
      return this.http.post(
        `${this.apiUrl}/pdf/standort/${standortId}`,
        {
          planImage: planImageBase64,
          useSavedImage: useSavedImage || false
        },
        {
          responseType: 'blob'
        }
      );
    }
    
    // Use GET for small images or when using saved image or no plan
    let params = new HttpParams();
    if (useSavedImage !== undefined) {
      params = params.set('useSavedImage', useSavedImage.toString());
    }
    if (planImageBase64 && !useSavedImage) {
      params = params.set('planImage', planImageBase64);
    }
    
    return this.http.get(`${this.apiUrl}/pdf/standort/${standortId}`, {
      params: params,
      responseType: 'blob'
    });
  }

}

